/**
 * Redaction and error normalisation.
 *
 * Why this exists: providers/core/requests.js rejects with `new Error(request.response)`,
 * so the raw server response body becomes the error message. The API's exception filter
 * makes that body `{ Message, Source, ExceptionType }` -- server exception text, which in a
 * people-and-companies domain can carry names, emails and phone numbers. That text used to
 * go only to an email inbox; once it reaches Application Insights it sits in
 * exceptions.outerMessage for the full retention period. Everything sent is redacted and
 * truncated here first.
 *
 * Dimension rule: ids (PersonId, CompanyId, RegistrationId) are fine. Names, emails, phone
 * numbers and addresses are not.
 */
export const MAX_TEXT_LENGTH = 2000;
export const MAX_STACK_LENGTH = 4000;

const EMAIL = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
// 10+ consecutive digits: phone numbers and card fragments. Record ids in this app are
// shorter than that, so they survive and stay useful for grouping.
const LONG_NUMBER = /\d{10,}/g;

export function truncate(text, max = MAX_TEXT_LENGTH) {
  if (typeof text !== 'string' || text.length <= max) return text;
  return `${text.slice(0, max)}... [truncated ${text.length - max} chars]`;
}

export function redact(text, max = MAX_TEXT_LENGTH) {
  if (typeof text !== 'string' || text === '') return text;
  return truncate(
    text.replace(EMAIL, '{email}').replace(LONG_NUMBER, '{number}'),
    max
  );
}

/**
 * Coerces anything a `catch` block might hold into a real Error with a usable stack.
 * Returns the error plus an ErrorKind describing what was actually thrown, so the
 * shapes that used to be dropped silently are now visible in telemetry.
 */
export function toError(input) {
  if (input instanceof Error) {
    return { error: input, kind: 'Error' };
  }

  if (typeof input === 'string') {
    // e.g. Communication/ImportEmail.js passes error.message rather than the error.
    return { error: new Error(input), kind: 'String' };
  }

  if (input && typeof input === 'object' && typeof input.message === 'string') {
    const error = new Error(input.message);
    if (typeof input.stack === 'string') error.stack = input.stack;
    if (typeof input.name === 'string') error.name = input.name;
    return { error, kind: 'ErrorLike' };
  }

  if (input === null || input === undefined) {
    // The old implementation destructured { message, stack } off this and threw a
    // TypeError from inside the error handler.
    return { error: new Error('Unknown client error'), kind: 'Missing' };
  }

  let described;
  try {
    described = JSON.stringify(input);
  } catch {
    described = String(input);
  }
  return {
    error: new Error(truncate(described, 512)),
    kind: typeof input === 'object' ? 'Object' : typeof input,
  };
}

/**
 * The API's ErrorHandlingExceptionFilterAttribute returns `{ Message, Source, ExceptionType }`
 * and requests.js puts that JSON straight into Error.message. Pulling it apart gives a stable
 * grouping key (the exception type) and moves the free text into a dimension that queries can
 * leave out. A bare JSON string body -- e.g. the invalid-email message, which arrives quoted --
 * is unwrapped to the string it contains.
 *
 * Returns null when the message is not a server payload.
 */
export function parseServerError(message) {
  if (typeof message !== 'string') return null;

  const trimmed = message.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('"')) return null;

  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }

  if (typeof parsed === 'string') {
    return { message: parsed };
  }

  if (!parsed || typeof parsed !== 'object') return null;

  const pick = (...names) => {
    const key = names.find(
      (name) =>
        Object.prototype.hasOwnProperty.call(parsed, name) &&
        typeof parsed[name] === 'string'
    );
    return key ? parsed[key] : undefined;
  };

  const exceptionType = pick('ExceptionType', 'exceptionType');
  const source = pick('Source', 'source');
  const serverMessage = pick('Message', 'message');

  if (!exceptionType && !source && serverMessage === undefined) return null;

  return { exceptionType, source, message: serverMessage };
}

/**
 * Returns a copy of the error with message and stack redacted. The stack is copied
 * because its first line repeats the message, so scrubbing only the message would
 * leave the original text in the stack.
 */
export function scrubError(error) {
  if (!(error instanceof Error)) return error;

  const scrubbed = new Error(redact(error.message));
  scrubbed.name = error.name;
  if (typeof error.stack === 'string') {
    scrubbed.stack = redact(error.stack, MAX_STACK_LENGTH);
  }
  return scrubbed;
}

/** Strips a query string from a URL, keeping origin and path. */
export function stripQuery(url) {
  if (typeof url !== 'string' || url === '') return url;
  return url.split(/[?#]/)[0];
}

// Exception type names are a bounded set, so they are safe to put in a message template.
// Anything that does not look like one is rejected rather than trusted, otherwise a stray id
// in the field would make the grouping key unbounded again.
const TYPE_NAME = /^[A-Za-z_][A-Za-z0-9_`+]*$/;

function shortTypeName(fullName) {
  if (typeof fullName !== 'string') return null;
  const last = fullName.trim().split('.').pop();
  return last && TYPE_NAME.test(last) && last.length <= 80 ? last : null;
}

/**
 * Turns whatever a catch block was holding into everything needed to report it: a real
 * scrubbed Error, a bounded message template, and structured dimensions.
 *
 * The template matters more than it looks. requests.js puts the API's
 * `{ Message, Source, ExceptionType }` JSON straight into Error.message, so using the message
 * as the signature would make every client failure unique and the new-error-signature detector
 * would fire forever. Pulling the exception type out gives a key that is stable per fault class
 * -- "No person 42" and "No person 99" both become ItemNotFoundException -- and moves the free
 * text into a dimension a query can leave out.
 *
 * Returns { error, template, properties, matchText }, where matchText is the innermost human
 * message for suppression matching (unquoted, un-redacted, caller-normalised).
 */
export function normalizeError(input) {
  const { error: rawError, kind } = toError(input);
  const server = parseServerError(rawError.message);
  const error = scrubError(rawError);

  const properties = { ErrorKind: kind };
  let template = 'Client error (unclassified)';

  const exceptionType = shortTypeName(server?.exceptionType);
  if (exceptionType) {
    template = `Client error: ${exceptionType}`;
    properties.ExceptionType = exceptionType;
  } else if (kind === 'Error' || kind === 'ErrorLike') {
    // No server payload: the JS error name is still bounded and better than nothing.
    properties.ExceptionType = rawError.name;
  }

  if (server?.source) properties.Source = redact(server.source, 200);
  if (server?.message !== undefined) {
    properties.ServerMessage = redact(server.message);
  }

  return {
    error,
    template,
    properties,
    // Deliberately the un-redacted innermost message: suppression compares against literal
    // strings such as the invalid-email message, which arrives JSON-quoted from the API.
    matchText: server?.message ?? rawError.message,
  };
}
